import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { ComposeEmailModal } from './ComposeEmailModal';

interface Contact {
  id: number;
  full_name: string;
  email: string | null;
  job_title: string | null;
  linkedin_url: string | null;
  phone: string | null;
  priority: number;
  legacy_label: string | null;
  stage: string;
  source: string;
  notes: string | null;
  company_id: number | null;
  company_id_join: number | null;
  company_name: string | null;
  company_domain: string | null;
  company_customer_user_id: number | null;
  created_at: string;
  updated_at: string;
}

interface Demo {
  id: number;
  scheduled_at: string;
  duration_mins: number | null;
  outcome: string;
  title: string | null;
  notes: string | null;
  deal_id: number | null;
}

interface Task {
  id: number;
  title: string;
  task_type: string;
  due_date: string;
  completed_at: string | null;
  notes: string | null;
  company_id: number | null;
  deal_id: number | null;
}

interface DealSummary {
  id: number;
  name: string;
  stage: string;
  value_pence: number | null;
  won_at: string | null;
  lost_at: string | null;
  created_at: string;
}

interface Email {
  id: number;
  external_id: string;
  thread_id: string | null;
  subject: string | null;
  body_preview: string | null;
  from_email: string | null;
  from_name: string | null;
  to_emails: Array<{ email: string; name: string | null }>;
  direction: 'inbound' | 'outbound';
  ai_importance_score: number | null;
  ai_category: string | null;
  ai_summary: string | null;
  sent_at: string;
}

interface ContactDetailResp {
  contact: Contact;
  demos: Demo[];
  tasks: Task[];
  deals: DealSummary[];
  emails: Email[];
}

const authHeaders = (): HeadersInit => ({
  Authorization: `Bearer ${localStorage.getItem('token')}`,
  'Content-Type': 'application/json'
});

const formatDate = (iso: string | null | undefined): string => iso ? new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : '—';
const formatDateTime = (iso: string | null | undefined): string => iso ? new Date(iso).toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : '—';
const formatDaysAgo = (iso: string | null | undefined): string => {
  if (!iso) return '—';
  const days = Math.floor((Date.now() - new Date(iso).getTime()) / 86400000);
  if (days === 0) return 'today';
  if (days === 1) return 'yesterday';
  if (days < 0) return `in ${-days}d`;
  return `${days}d ago`;
};
const formatPence = (p: number | null | undefined): string => {
  if (p == null) return '—';
  return new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP', maximumFractionDigits: 0 }).format(p / 100);
};

export const ContactDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [data, setData] = useState<ContactDetailResp | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [replyingTo, setReplyingTo] = useState<Email | null>(null);
  const [composeNew, setComposeNew] = useState(false);
  const [showAddTask, setShowAddTask] = useState(false);

  const load = async () => {
    if (!id) return;
    setLoading(true);
    try {
      const res = await fetch(`/api/crm/contacts/${id}`, { headers: authHeaders() });
      if (!res.ok) throw new Error('Failed to load contact');
      setData(await res.json());
      setError(null);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [id]);

  const updatePriority = async (next: number) => {
    if (!data) return;
    setData({ ...data, contact: { ...data.contact, priority: next } });
    await fetch(`/api/crm/prospects/${data.contact.id}`, {
      method: 'PATCH', headers: authHeaders(), body: JSON.stringify({ priority: next })
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mb-4"></div>
          <p className="text-gray-600">Loading contact…</p>
        </div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="min-h-screen bg-gray-50 p-8">
        <div className="max-w-4xl mx-auto">
          <button onClick={() => navigate('/admin/crm')} className="text-blue-600 hover:underline text-sm mb-4">← Back to CRM</button>
          <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-red-800">Failed to load contact. {error}</div>
        </div>
      </div>
    );
  }

  const c = data.contact;
  const openTasks = data.tasks.filter(t => !t.completed_at);
  const completedTasks = data.tasks.filter(t => t.completed_at);

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-6xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <button onClick={() => navigate('/admin/crm')} className="text-xs text-blue-600 hover:underline">← All contacts</button>
            <h1 className="text-3xl font-bold text-gray-900 mt-1">{c.full_name}</h1>
            <p className="text-sm text-gray-500 mt-0.5 flex items-center gap-2">
              {c.job_title && <span>{c.job_title}</span>}
              {c.company_name && c.company_id && (
                <>
                  {c.job_title && <span>·</span>}
                  <Link to={`/admin/crm/companies/${c.company_id}`} className="text-blue-600 hover:underline">{c.company_name}</Link>
                </>
              )}
              {c.company_customer_user_id && (
                <span className="px-1.5 py-0.5 bg-green-100 text-green-800 rounded text-[10px] font-semibold">CUSTOMER</span>
              )}
            </p>
          </div>
          <div className="flex items-center gap-2">
            {c.email && (
              <button onClick={() => setComposeNew(true)}
                className="px-3 py-2 text-sm bg-blue-600 text-white rounded hover:bg-blue-700">
                ✉ Email
              </button>
            )}
            <button onClick={load} className="px-3 py-2 text-sm border border-gray-300 rounded hover:bg-gray-50">Refresh</button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <StatTile label="Open tasks" value={openTasks.length} sub={completedTasks.length > 0 ? `${completedTasks.length} done` : ''} />
          <StatTile label="Deals" value={data.deals.length} sub={`${data.deals.filter(d => d.won_at).length}W / ${data.deals.filter(d => d.lost_at).length}L`} />
          <StatTile label="Demos" value={data.demos.length} sub={data.demos[0] ? `last ${formatDaysAgo(data.demos[0].scheduled_at)}` : 'none'} />
          <StatTile label="Emails" value={data.emails.length} sub={data.emails[0] ? `last ${formatDaysAgo(data.emails[0].sent_at)}` : 'none'} />
        </div>

        {/* Identity */}
        <Section title="Contact details">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
            <DetailRow label="Email" value={c.email} email />
            <DetailRow label="Phone" value={c.phone} />
            <DetailRow label="LinkedIn" value={c.linkedin_url} link />
            <DetailRow label="Job title" value={c.job_title} />
            <DetailRow label="Source" value={c.source} />
            <div>
              <div className="text-xs text-gray-500 mb-1">Priority</div>
              <div className="flex items-center gap-1">
                {[0,1,2,3,4,5].map(n => (
                  <button key={n} onClick={() => updatePriority(n)}
                    className={`px-1.5 py-0.5 rounded text-xs ${
                      c.priority === n ? 'bg-amber-100 ring-1 ring-amber-400' : 'hover:bg-gray-100'
                    }`}
                    title={n === 0 ? 'Clear' : `Set priority ${n}/5`}>
                    {n === 0 ? '✕' : '🔥'.repeat(n)}
                  </button>
                ))}
              </div>
            </div>
            {c.legacy_label && (
              <div>
                <div className="text-xs text-gray-500 mb-1">Era</div>
                <span className="px-1.5 py-0.5 bg-amber-100 text-amber-800 rounded text-xs font-medium">{c.legacy_label}</span>
              </div>
            )}
          </div>
          {c.notes && (
            <div className="mt-4">
              <div className="text-xs text-gray-500 mb-1">Notes</div>
              <p className="text-sm text-gray-700 whitespace-pre-wrap bg-gray-50 rounded p-3">{c.notes}</p>
            </div>
          )}
        </Section>

        {/* Tasks */}
        <Section title={`Tasks (${openTasks.length} open)`}
          right={
            <button onClick={() => setShowAddTask(true)}
              className="px-2 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700">+ Task</button>
          }
        >
          {openTasks.length === 0 && completedTasks.length === 0 ? (
            <p className="text-sm text-gray-400 italic">No tasks for this contact yet.</p>
          ) : (
            <>
              {openTasks.length > 0 && (
                <ul className="space-y-1.5">
                  {openTasks.map(t => <TaskRow key={t.id} task={t} onChanged={load} />)}
                </ul>
              )}
              {completedTasks.length > 0 && (
                <details className="mt-3">
                  <summary className="text-xs text-gray-500 cursor-pointer hover:text-gray-700">
                    Completed ({completedTasks.length})
                  </summary>
                  <ul className="mt-2 space-y-1.5">
                    {completedTasks.map(t => <TaskRow key={t.id} task={t} onChanged={load} />)}
                  </ul>
                </details>
              )}
            </>
          )}
        </Section>

        {/* Emails */}
        <Section title={`Emails (${data.emails.length})`}>
          {data.emails.length === 0 ? (
            <p className="text-sm text-gray-400 italic">No emails linked to this contact yet.</p>
          ) : (
            <ul className="divide-y divide-gray-100">
              {data.emails.slice(0, 25).map(e => {
                const meta = e.ai_category ? CATEGORY_META[e.ai_category] : null;
                return (
                  <li key={e.id} className="py-2 flex items-start gap-3 text-sm group">
                    <span title={e.direction === 'inbound' ? 'Received' : 'Sent'}
                      className={`shrink-0 mt-1 inline-block w-2 h-2 rounded-full ${e.direction === 'inbound' ? 'bg-blue-500' : 'bg-gray-300'}`} />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-medium text-gray-900 truncate">{e.subject || '(no subject)'}</span>
                        {meta && <span className={`px-1 py-0.5 rounded text-[10px] ${meta.cls}`}>{meta.label}</span>}
                        {e.ai_importance_score != null && (
                          <span className="text-[10px] text-gray-500">{e.ai_importance_score}/100</span>
                        )}
                      </div>
                      <div className="text-xs text-gray-500">{formatDateTime(e.sent_at)}</div>
                      {e.ai_summary && <p className="text-xs text-gray-600 mt-1 italic">🧠 {e.ai_summary}</p>}
                      {e.body_preview && <p className="text-xs text-gray-600 mt-1 line-clamp-2">{e.body_preview}</p>}
                    </div>
                    {e.direction === 'inbound' && e.from_email && (
                      <button onClick={() => setReplyingTo(e)}
                        className="opacity-0 group-hover:opacity-100 transition-opacity shrink-0 text-xs px-2 py-1 bg-blue-600 text-white rounded hover:bg-blue-700">
                        Reply
                      </button>
                    )}
                  </li>
                );
              })}
              {data.emails.length > 25 && (
                <li className="py-2 text-xs text-gray-400 italic">+ {data.emails.length - 25} older emails</li>
              )}
            </ul>
          )}
        </Section>

        {/* Deals */}
        <Section title={`Deals (${data.deals.length})`}>
          {data.deals.length === 0 ? (
            <p className="text-sm text-gray-400 italic">No deals where this contact is primary.</p>
          ) : (
            <ul className="divide-y divide-gray-100">
              {data.deals.map(d => (
                <li key={d.id} className="py-2 flex items-center justify-between text-sm">
                  <div>
                    <div className="font-medium text-gray-900">{d.name}</div>
                    <div className="text-xs text-gray-500">Created {formatDate(d.created_at)}</div>
                  </div>
                  <div className="flex items-center gap-2 text-xs">
                    <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${
                      d.won_at ? 'bg-green-100 text-green-800' :
                      d.lost_at ? 'bg-gray-100 text-gray-600' :
                      'bg-blue-100 text-blue-800'
                    }`}>{d.stage}</span>
                    {d.value_pence != null && <span className="font-medium">{formatPence(d.value_pence)}</span>}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </Section>

        {/* Demos */}
        <Section title={`Demos (${data.demos.length})`}>
          {data.demos.length === 0 ? (
            <p className="text-sm text-gray-400 italic">No demos with this contact.</p>
          ) : (
            <ul className="divide-y divide-gray-100">
              {data.demos.slice(0, 20).map(d => (
                <li key={d.id} className="py-2 flex items-center justify-between text-sm">
                  <div>
                    <div className="font-medium text-gray-900">{d.title || 'Untitled demo'}</div>
                    <div className="text-xs text-gray-500">{formatDateTime(d.scheduled_at)}{d.duration_mins ? ` · ${d.duration_mins}min` : ''}</div>
                    {d.notes && <p className="text-xs text-gray-600 mt-1">{d.notes}</p>}
                  </div>
                  <span className={`px-1.5 py-0.5 rounded text-[10px] ${
                    d.outcome === 'completed' ? 'bg-green-100 text-green-800' :
                    d.outcome === 'no_show'   ? 'bg-red-100 text-red-800' :
                    d.outcome === 'cancelled' ? 'bg-gray-100 text-gray-600' :
                    'bg-blue-100 text-blue-800'
                  }`}>{d.outcome}</span>
                </li>
              ))}
            </ul>
          )}
        </Section>
      </div>

      {/* Modals */}
      {replyingTo && (
        <ComposeEmailModal
          replyToEmailId={replyingTo.id}
          to={replyingTo.from_email || undefined}
          toName={replyingTo.from_name || undefined}
          replyOriginalSubject={replyingTo.subject || undefined}
          replyOriginalPreview={replyingTo.body_preview || undefined}
          onClose={() => setReplyingTo(null)}
          onSent={() => { setReplyingTo(null); setTimeout(load, 1500); }}
        />
      )}
      {composeNew && c.email && (
        <ComposeEmailModal
          to={c.email}
          toName={c.full_name}
          onClose={() => setComposeNew(false)}
          onSent={() => { setComposeNew(false); setTimeout(load, 1500); }}
        />
      )}
      {showAddTask && (
        <AddTaskModal
          target={{ type: 'prospect', id: c.id, name: c.full_name }}
          onClose={() => setShowAddTask(false)}
          onSaved={() => { setShowAddTask(false); load(); }}
        />
      )}
    </div>
  );
};

const StatTile: React.FC<{ label: string; value: number | string; sub?: string }> = ({ label, value, sub }) => (
  <div className="bg-white rounded-lg shadow p-4">
    <div className="text-xs uppercase tracking-wider text-gray-500 font-semibold">{label}</div>
    <div className="text-2xl font-bold text-gray-900 mt-1">{value}</div>
    {sub && <div className="text-xs text-gray-500 mt-1">{sub}</div>}
  </div>
);

const Section: React.FC<{ title: string; right?: React.ReactNode; children: React.ReactNode }> = ({ title, right, children }) => (
  <div className="bg-white rounded-lg shadow p-5">
    <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
      <h2 className="text-lg font-semibold text-gray-900">{title}</h2>
      {right}
    </div>
    {children}
  </div>
);

const DetailRow: React.FC<{ label: string; value: string | null; email?: boolean; link?: boolean }> = ({ label, value, email, link }) => (
  <div>
    <div className="text-xs text-gray-500 mb-1">{label}</div>
    {value ? (
      email ? <a href={`mailto:${value}`} className="text-blue-600 hover:underline">{value}</a> :
      link  ? <a href={value} target="_blank" rel="noreferrer" className="text-blue-600 hover:underline truncate block">{value}</a> :
      <span className="text-gray-900">{value}</span>
    ) : <span className="text-gray-400">—</span>}
  </div>
);

const TaskRow: React.FC<{ task: Task; onChanged: () => void }> = ({ task, onChanged }) => {
  const done = !!task.completed_at;
  const overdue = !done && new Date(task.due_date).getTime() < Date.now();
  const toggle = async () => {
    await fetch(`/api/crm/tasks/${task.id}`, {
      method: 'PATCH', headers: authHeaders(),
      body: JSON.stringify({ completed: !done })
    });
    onChanged();
  };
  const del = async () => {
    if (!window.confirm(`Delete task "${task.title}"?`)) return;
    await fetch(`/api/crm/tasks/${task.id}`, { method: 'DELETE', headers: authHeaders() });
    onChanged();
  };
  return (
    <li className={`flex items-center gap-2 text-xs rounded px-3 py-2 ${done ? 'bg-gray-50' : overdue ? 'bg-red-50' : 'bg-amber-50'}`}>
      <input type="checkbox" checked={done} onChange={toggle} className="cursor-pointer" />
      <div className="flex-1 min-w-0">
        <div className={`font-medium ${done ? 'text-gray-500 line-through' : 'text-gray-900'}`}>{task.title}</div>
        <div className={`text-[10px] ${overdue ? 'text-red-600 font-semibold' : 'text-gray-500'}`}>
          Due {formatDateTime(task.due_date)}
          {task.task_type === 'follow_up' && ' · auto from demo'}
          {overdue && ' · OVERDUE'}
        </div>
        {task.notes && <p className="text-gray-600 mt-1">{task.notes}</p>}
      </div>
      <button onClick={del} className="text-gray-300 hover:text-red-600 text-sm" title="Delete">×</button>
    </li>
  );
};

// ============================================================
// Add task modal — reusable across contact / company / deal pages
// ============================================================

export interface TaskTarget {
  type: 'prospect' | 'company' | 'deal';
  id: number;
  name: string;
}

export const AddTaskModal: React.FC<{
  target: TaskTarget;
  onClose: () => void;
  onSaved: () => void;
}> = ({ target, onClose, onSaved }) => {
  const [title, setTitle] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const save = async () => {
    if (!title.trim() || !dueDate) { setError('Title and due date required'); return; }
    setSaving(true);
    setError(null);
    try {
      // Route by target type — backend has POST /prospects/:id/tasks,
      // /companies/:id/tasks, /deals/:id/tasks.
      const url = `/api/crm/${target.type === 'prospect' ? 'prospects' : target.type === 'company' ? 'companies' : 'deals'}/${target.id}/tasks`;
      const res = await fetch(url, {
        method: 'POST', headers: authHeaders(),
        body: JSON.stringify({
          title: title.trim(),
          dueDate: new Date(dueDate).toISOString(),
          notes: notes.trim() || null
        })
      });
      if (!res.ok) {
        const e = await res.json();
        throw new Error(e.error || 'Failed');
      }
      onSaved();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50" onClick={onClose}>
      <div className="bg-white rounded-xl max-w-md w-full" onClick={e => e.stopPropagation()}>
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <div>
            <h2 className="text-xl font-bold text-gray-900">Add task</h2>
            <p className="text-xs text-gray-500 mt-0.5">For {target.name}</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700 text-2xl">×</button>
        </div>
        <div className="px-6 py-4 space-y-3">
          {error && <div className="bg-red-50 border border-red-200 rounded p-2 text-xs text-red-800">{error}</div>}
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Task</label>
            <input type="text" autoFocus value={title} onChange={e => setTitle(e.target.value)}
              placeholder="Send pricing proposal"
              className="w-full px-3 py-2 border border-gray-300 rounded text-sm" />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Due</label>
            <input type="datetime-local" value={dueDate} onChange={e => setDueDate(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded text-sm" />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Notes (optional)</label>
            <textarea value={notes} onChange={e => setNotes(e.target.value)} rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded text-sm" />
          </div>
        </div>
        <div className="px-6 py-3 border-t border-gray-200 flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 text-sm border border-gray-300 rounded hover:bg-gray-50">Cancel</button>
          <button onClick={save} disabled={saving || !title.trim() || !dueDate}
            className="px-4 py-2 bg-blue-600 text-white rounded text-sm font-medium hover:bg-blue-700 disabled:opacity-50">
            {saving ? 'Saving…' : 'Add task'}
          </button>
        </div>
      </div>
    </div>
  );
};

// Category badge metadata (shared with Awaiting Reply)
const CATEGORY_META: Record<string, { label: string; cls: string }> = {
  lead_inquiry:    { label: 'Lead',         cls: 'bg-green-100 text-green-800' },
  customer_reply:  { label: 'Customer',     cls: 'bg-blue-100 text-blue-800' },
  scheduling:      { label: 'Scheduling',   cls: 'bg-cyan-100 text-cyan-800' },
  support:         { label: 'Support',      cls: 'bg-purple-100 text-purple-800' },
  cold_outreach:   { label: 'Cold pitch',   cls: 'bg-amber-100 text-amber-800' },
  newsletter:      { label: 'Newsletter',   cls: 'bg-gray-100 text-gray-600' },
  transactional:   { label: 'Transactional',cls: 'bg-gray-100 text-gray-600' },
  internal:        { label: 'Internal',     cls: 'bg-indigo-100 text-indigo-800' },
  personal:        { label: 'Personal',     cls: 'bg-pink-100 text-pink-800' },
  unknown:         { label: 'Unknown',      cls: 'bg-gray-100 text-gray-500' }
};
